import {ENROLMENT_TYPE, QUESTION_TYPE} from '@/common/consts/survey.js';
import {
    deleteAllAnswers,
    deleteEnrolmentAnswer,
    getEnrolmentsAnswers,
    postEnrolmentAnswer,
} from '@/common/apis/enrolment/responseApi.js';
import {toDateFromISOFormat} from '@/common/utils/date.js';
import {ANSWER_TYPE, SURVEY_ANSWER_STATE_TYPE} from '@/common/consts/response.js';
import {getInitForm} from '@/common/store/modules/enrolment/editor.js';
import i18n from '@common/i18n.js';
import {convertToLatestEnrolmentPayload} from '@/common/store/modules/enrolment/payload.js';
import {getPeriodStateType} from '@/common/store/modules/common/response/response';
import {pipe} from "@common/utils/functionalUtil";
import ACCEPTED_DOMAIN from "@common/consts/accepted-domain";

export const GET_ENROLMENT_TYPE_NAME = 'GET_ENROLMENT_TYPE_NAME';
export const IS_BEFORE_ENROLMENT_PERIOD =  'IS_BEFORE_ENROLMENT_PERIOD';
export const IS_AFTER_ENROLMENT_PERIOD = 'IS_AFTER_ENROLMENT_PERIOD';
export const IS_AVAILABLE_ENROLMENT_PERIOD = 'IS_AVAILABLE_ENROLMENT_PERIOD';
export const IS_ALL_AGREEMENTS_CHECKED = 'IS_ALL_AGREEMENTS_CHECKED';

export const SHOW_ERROR = 'SHOW_ERROR';
export const CLOSE_ERROR = 'CLOSE_ERROR';
export const INIT_ENROLMENT_DATA = 'INIT_ENROLMENT_DATA';
export const CHANGE_ANSWER_STATE = 'CHANGE_ANSWER_STATE';
export const APPLY_ANSWER = 'APPLY_ANSWER';
export const CANCEL_ANSWER = 'CANCEL_ANSWER';
export const FORCE_CLOSE = 'FORCE_CLOSE';
export const SET_FILE_NOW_ORDER_NO = 'SET_FILE_NOW_ORDER_NO';
export const CHECK_AGREEMENTS = 'CHECK_AGREEMENTS';
export const CHECK_ALL_AGREEMENTS = 'CHECK_ALL_AGREEMENTS';
export const UNCHECK_AGREEMENT = 'UNCHECK_AGREEMENT';
export const UNCHECK_ALL_AGREEMENTS = 'UNCHECK_ALL_AGREEMENTS';

const store = {
    namespaced: true,

    state: {
        survey: {},
        prevProgramOrder: {},
        options: {},
        error: getNoError(),
    },

    getters: {
        /**
         * 수강신청 타입(추첨, 선착순) 이름 리턴
         * @param state
         * @returns {string}
         */
        [GET_ENROLMENT_TYPE_NAME](state) {
            switch (state.survey.enrolmentType) {
                case ENROLMENT_TYPE.RANDOM:
                    return i18n.t('EnrolmentEditor_randomMethod');

                case ENROLMENT_TYPE.FCFS:
                    return i18n.t('EnrolmentEditor_fcfsMethod');

                case ENROLMENT_TYPE.DRAW_PRIOR:
                    return i18n.t('EnrolmentResponse_authDrawing');
            }
        },

        /**
         * 수강신청 시작 전
         * @param state
         * @returns {boolean}
         */
        [IS_BEFORE_ENROLMENT_PERIOD](state) {
            const startDate = toDateFromISOFormat(state.survey.startDate);
            return Date.now() < startDate;
        },

        /**
         * 수강신청 종료 후
         * @param state
         * @returns {boolean}
         */
        [IS_AFTER_ENROLMENT_PERIOD](state) {
            const endDate = toDateFromISOFormat(state.survey.endDate);
            return state.options.forceClose || Date.now() > endDate;
        },

        /**
         * 수강신청 기간 여부
         * @param state
         * @param getters
         * @returns {boolean}
         */
        [IS_AVAILABLE_ENROLMENT_PERIOD](state, getters) {
            return !(getters.IS_BEFORE_ENROLMENT_PERIOD) && !(getters.IS_AFTER_ENROLMENT_PERIOD);
        },

        /**
         * 모든 동의문 체크 여부
         * @param state
         * @returns {boolean}
         */
        [IS_ALL_AGREEMENTS_CHECKED](state) {
            return state.options.checkedAgreementsNos.length === state.survey.contents?.agreements?.length;
        },
    },

    mutations: {
        forceClose(state) {
            state.options.forceClose = true;
        },

        setError(state, {hasError, errorType, resultCode, resultMessage}) {
            state.error = {
                hasError,
                errorType,
                resultCode,
                resultMessage,
            }
        },

        closeError(state) {
            state.error = getNoError();
        },

        /**
         * 수강신청 데이터 초기화
         * @param state
         * @param data
         */
        initEnrolmentData(state, {data, isKeepOrder = false}) {
            state.survey = getInitData({
                originData: data,
                isKeepOrder, prevProgramOrder:
                state.prevProgramOrder,
            });

            if (state.survey.answerStateType === SURVEY_ANSWER_STATE_TYPE.ANSWER) {
                state.options.checkedAgreementsNos = state.survey.contents.agreements.map(agreement => agreement.questionNo);
            }
            state.prevProgramOrder = {};
            state.survey.contents.questions.forEach(question => {
                state.prevProgramOrder[question.questionNo] = question.programs.map(program => program.programNo);
            });
        },

        /**
         * 수강신청 미리보기 데이터 초기화
         * @param state
         * @param previewData
         */
        initPreviewEnrolmentData(state, previewData) {
            state.survey = state.options.previewMode === 'popup' ? getInitPreviewData(previewData) : getInitPreviewFormData(previewData);
        },

        /**
         * 옵션 초기화
         * @param state
         * @param payload
         */
        setOptions(state, payload) {
            state.options = {
                surveyReferenceNo: payload.surveyReferenceNo,
                receiverReferenceNo:payload.receiverReferenceNo,
                provider: payload.provider,
                isMasking: payload.isMasking,
                isPreview: payload.isPreview,
                previewMode: payload.previewMode,
                forceClose: false,
                checkedAgreementsNos: [],
            };
        },

        /**
         * 신청상태 변경
         * @param state
         * @param answerStateType
         */
        changeAnswerState(state, {answerStateType}) {
            state.survey.answerStateType = answerStateType;
        },

        /**
         * 신청기간 상태 변경
         * @param state
         * @param periodStateType
         */
        changePeriodState(state) {
            state.survey.periodStateType = getPeriodStateType(state.survey);
        },

        /**
         * 해당 프로그램의 로딩상태 설정(set)
         * @param state
         * @param programNo
         */
        setLoading(state, {programNo}) {
            state.survey.contents.questions.forEach(question => {
                const program = question.programs.find(program => program.programNo === programNo);
                if (program) {
                    program.isLoading = true;
                }
            })
        },

        /**
         * 해당 프로그램의 로딩상태 해제(unset)
         * @param state
         * @param programNo
         */
        unsetLoading(state, {programNo}) {
            state.survey.contents.questions.forEach(question => {
                const program = question.programs.find(program => program.programNo === programNo);
                if (program) {
                    program.isLoading = false;
                }
            })
        },

        /**
         * 프로그램 데이터 변경
         * @param state
         * @param data
         */
        setProgramAnswer(state, data) {
            changeProgramAnswer({
                originSurvey: state.survey,
                newData: data,
                programNo: data.programNo
            });
        },

        setFileNowOrderNo(state, {nowOrderNo}) {
            state.survey.contents.documents[0].nowOrderNo = nowOrderNo;
        },

        checkAgreements(state, {questionNos}) {
            state.options.checkedAgreementsNos.push(...questionNos);
        },
        checkAllAgreements(state) {
            const questionNos = state.survey.contents.agreements.map(agreement => agreement.questionNo);
            state.options.checkedAgreementsNos = [...questionNos];
        },
        uncheckAgreement(state, {questionNo}) {
            state.options.checkedAgreementsNos = state.options.checkedAgreementsNos.filter(checkedAgreementNo => checkedAgreementNo !== questionNo);
        },
        uncheckAllAgreements(state) {
            state.options.checkedAgreementsNos = [];
        },
    },

    actions: {
        [FORCE_CLOSE]({commit}) {
            commit('forceClose');
        },

        /**
         * Error 노출
         * @param commit
         * @param hasError
         * @param errorType
         * @param resultCode
         * @param resultMessage
         */
        [SHOW_ERROR]({commit}, {hasError, errorType, resultCode, resultMessage}) {
            commit('setError', {
                hasError,
                errorType,
                resultCode,
                resultMessage,
            });
        },

        /**
         * Error 미노출
         * @param commit
         */
        [CLOSE_ERROR]({commit}) {
            commit('closeError');
        },

        /**
         * 수강신청 전체 데이터 로드
         * @param commit
         */
        [INIT_ENROLMENT_DATA]: async ({commit}, payload) => {
            commit('setOptions', payload);
            if (payload.isPreview) {
                const previewData = await getEnrolmentsAnswersForPreview(payload);
                commit('initPreviewEnrolmentData', previewData);
            }

            else {
                const data = await getEnrolmentsAnswers(payload);
                commit('initEnrolmentData', {data});
            }
        },

        /**
         * 신청상태 변경
         * @param commit
         * @param answerStatusType
         */
        [CHANGE_ANSWER_STATE](context, {answerStateType}) {
            context.commit('changePeriodState');

            if (context.state.options.isPreview) {
                context.commit('changeAnswerState', {answerStateType});
                return;
            }

            if (answerStateType === SURVEY_ANSWER_STATE_TYPE.DENY) {
                deleteAllAnswers({
                    surveyNo: context.state.survey.surveyNo,
                    surveyReceiverNo: context.state.survey.receiver.surveyReceiverNo
                })
                .finally(() => getEnrolmentsAnswers(context.state.options)
                    .then(data => context.commit('initEnrolmentData', {data, isKeepOrder: true}))
                .catch(err => console.log(err)));

                return;
            }
            context.commit('changeAnswerState', {answerStateType});
        },

        /**
         * 프로그램 신청, 해당 프로그램 정보 갱신
         * @param context
         * @param programNo
         */
        async [APPLY_ANSWER]({state, commit}, {programNo}) {
            commit('changePeriodState');

            const surveyNo = state.survey.surveyNo;
            const data = {
                surveyReferenceNo: state.survey.surveyReferenceNo,
                questionNo: programNo,
                surveyReceiverNo: state.survey.receiver.surveyReceiverNo,
                isFCFS: state.survey.enrolmentType === ENROLMENT_TYPE.FCFS,
                answerNo: 0,
                answer: 1,
            };

            commit('setLoading', {programNo});

            try {
                await postEnrolmentAnswer({surveyNo, data});
            } finally {
                const enrolmentData = await getEnrolmentsAnswers(state.options);

                commit('initEnrolmentData', {data: enrolmentData, isKeepOrder: true});
                commit('unsetLoading', {programNo})
            }
        },

        /**
         * 프로그램 신청 취소, 해당 프로그램 정보 갱신
         * @param context
         * @param surveyAnswerNo
         */
        async [CANCEL_ANSWER]({state, commit}, {surveyAnswerNo, programNo}) {
            commit('changePeriodState');

            const surveyNo = state.survey.surveyNo;
            const surveyReceiverNo = state.survey.receiver.surveyReceiverNo;

            commit('setLoading', {programNo});

            try {
                await deleteEnrolmentAnswer({
                    surveyAnswerNo: surveyAnswerNo,
                    surveyReceiverNo: surveyReceiverNo,
                    surveyNo: surveyNo,
                });
            } finally {
                const enrolmentData = await getEnrolmentsAnswers(state.options);

                commit('initEnrolmentData', {data: enrolmentData, isKeepOrder: true});
                commit('unsetLoading', {programNo})
            }

        },

        /**
         * 이미지 뷰어에서 파일뷰어 전환시 현재 노출된 페이지번호를 동기화 시키기 위한 액션
         * @param commit
         * @param nowOrderNo
         */
        [SET_FILE_NOW_ORDER_NO]({commit}, {nowOrderNo}) {
            commit('setFileNowOrderNo', {nowOrderNo});
        },

        /** 동의문 체크 */
        [CHECK_AGREEMENTS]({commit}, {questionNos}) {
            commit('checkAgreements', {questionNos});
        },
        /** 모든 동의문 체크 */
        [CHECK_ALL_AGREEMENTS]({commit}) {
            commit('checkAllAgreements');
        },
        /** 동의문 체크 해제 */
        [UNCHECK_AGREEMENT]({commit}, {questionNo}) {
            commit('uncheckAgreement', {questionNo});
        },
        /** 모든 동의문 체크 */
        [UNCHECK_ALL_AGREEMENTS]({commit}) {
            commit('uncheckAllAgreements');
        }
    }
};

const getHasReceiverFilterTagFunc = receiverFilterTag => programs =>
    programs.filter(program => program.filters.includes(receiverFilterTag));

const getProgramMapFunc = (answers, drawResults, originQuestions) => programs => programs.map(program => {
    const answer = answers.find((answer) => answer.questionNo === program.programNo);
    const drawResult = drawResults.find(result => result.questionNo === program.programNo);
    const originQuestion = originQuestions.find(originQuestion => originQuestion.questionNo === program.programNo);

    return {
        programNo: program.programNo,
        programDesc: program.programDesc || '',
        programNumber: program.programNumber,
        programName: program.programName,
        filters: program.filters,
        limit: program.limit,
        reservedLimit: program.reservedLimit,
        answer: answer && {
            surveyAnswerNo: answer.surveyAnswerNo,
            surveyReceiverNo: answer.surveyReceiverNo,
            answerType: answer.answer,
            order: answer.order,
            priority: answer.priority,
        },
        drawResult: drawResult && {
            drawResultType: drawResult.drawResultType,
            order: drawResult.order,
        },
        answerTotalCount: originQuestion && originQuestion.count,
        answerRegularCount: originQuestion && originQuestion.answerSummaries.find(summary => summary.answer === 'REGULAR').count,
        answerReservedCount: originQuestion && originQuestion.answerSummaries.find(summary => summary.answer === 'RESERVED').count,
        times: program.times,
        costs: program.costs,
        isLoading: false,
    };
})

const getProgramSortFunc = (question, isKeepOrder, prevProgramOrder) => programs => programs.sort((a, b) => compareProgram(a, b, {
    enrolmentType: question.enrolmentType,
    questionNo: question.questionNo,
    isKeepOrder,
    prevProgramOrder,
}))

/**
 * 서버로부터 받은 데이터(originData)를 사용하기 쉽게 변환하여 리턴
 * @param originData
 */
function getInitData({originData, isKeepOrder, prevProgramOrder}) {
    const isIamteacherapp = window.location.href.includes('iamteacherapp'); // iamteacherapp 인지 아닌지 구분

    const receiver = originData.surveyReceivers[0];
    const receiverFilterTag = !isIamteacherapp ? receiver.receiverOptions.filter(v => v.optionType === 'FILTER_TAG')[0].optionValue : false; // iamteacherapp 이면 grade 필터 X
    const contents = convertToLatestEnrolmentPayload(JSON.parse(originData.contents));
    const enrolmentQuestions = contents.questions.filter(question => !question.questionType || question.questionType === QUESTION_TYPE.ENROLMENT || question.questionType === QUESTION_TYPE.ENROLMENT_QUESTION);
    const enrolmentType = enrolmentQuestions[0].enrolmentType;
    const answers = receiver.surveyAnswers;
    const drawResults = receiver.drawResults;
    const originQuestions = originData.surveyQuestions;

    return {
        surveyNo: originData.surveyNo,
        provider: originData.provider,
        surveyReferenceNo: originData.surveyReferenceNo,
        surveyType: originData.surveyType,
        title: originData.title,
        surveyDesc: originData.surveyDesc,
        schoolName: receiver.receiverInfo,
        receiverName: receiver.receiverName,
        enrolmentType: enrolmentType,
        draw: originData.draw,
        modifyDate : originData.modifyDate,
        modified: originData.modified,

        contents: {
            type: contents.type,
            //수강신청 전체 메인 이미지
            images: contents.images && contents.images.map(image => {
                return {
                    imageNo: image.imageNo,
                    index: image.index,
                    url: image.url,
                };
            }),

            /**
             * 수강신청 전체 메인 이미지, 수강신청당 이미지 모두 포함
             */
            attachments: originData.attachments && originData.attachments.map(attachement => {
                return {
                    type: attachement.type,
                    description: attachement.description,
                    image: attachement.image && {
                        imageNo: attachement.image.imageNo,
                        url: attachement.image.url,
                    },
                    orderNo: attachement.orderNo,
                }
            }),

            /**
             * 수강신청 첨부파일
             */
            documents: originData.documents && originData.documents.map(document => {
                return {
                    /**
                     * 첨부파일 내용을 docConverter 로 변환한 이미지 묶음
                     */
                    attachments: document.attachements && document.attachements.map(attachement => {
                        return {
                            type: attachement.type,
                            description: attachement.description,
                            image: attachement.image && {
                                imageNo: attachement.image.imageNo,
                                url: attachement.image.url,
                            },
                            orderNo: attachement.orderNo,
                        }
                    }),
                    description: document.description,
                    downloadUrlForExternal: document.downloadUrlForExternal,
                    fileName: document.fileName,
                    fileSize: document.fileSize,
                    orderNo: document.orderNo,
                    nowOrderNo: 0, //파일 내 이미지뷰어에서 파일뷰어로 전환 시 현재 노출되는 페이지번호의 동기화를 위한 값
                }
            }),
            /**
             * 수강신청 동의문
             */
            agreements: contents.questions.filter(question => question.questionType === QUESTION_TYPE.ENROLMENT_AGREEMENT),
            /**
             * 개별 수강신청
             */
            questions: contents.questions.filter(question => !question.questionType || question.questionType === QUESTION_TYPE.ENROLMENT || question.questionType === QUESTION_TYPE.ENROLMENT_QUESTION)
                .map(question => ({
                    question: question.question,
                    maxChoice: question.maxChoice,
                    choiceCount: question.programs.filter(program => answers.find((answer) => answer.questionNo === program.programNo)).length,
                    questionNo: question.questionNo,
                    questionNumber: question.Number,
                    image: question.image && {
                        imageNo: question.image.imageNo,
                        index: question.image.index,
                        url: question.image.url,
                    },

                    /**
                     * 해당 수강신청에 포함된 프로그램
                     */
                    programs: pipe(
                        question.programs,
                        receiverFilterTag ? getHasReceiverFilterTagFunc(receiverFilterTag) : false,
                        getProgramMapFunc(answers, drawResults, originQuestions),
                        getProgramSortFunc(question, isKeepOrder, prevProgramOrder),
                    )
                }))
                .filter(question => question.programs.length > 0), //필터태그로 필터링되어 프로그램이 한개도 남지 않은 수강신청은 제거

            /**
             * 필터태그관련 정보(수강신청 전체에서 사용하는 필터태그에 대한 정보가 포함되어 있음)
             */
            filterInfo: {
                type: contents.filterInfo.type,
                filters: contents.filterInfo.filters.map(filter => {
                    return {
                        label: filter.label,
                        value: filter.value,
                    }
                })
            },
        },

        startDate: originData.startDate,
        endDate: originData.endDate,
        periodStateType: getPeriodStateType(originData),
        sendDate: originData.sendDate,
        registerDate: originData.registerDate,
        receiver: {
            ...originData.surveyReceivers[0],
            isRead: !!originData.surveyReceivers[0].readDate,
        },
        answerStateType: originData.surveyReceivers[0].answerStateType,
        options: originData.surveyOptions,
    };
}

/**
 * 수강신청 내 프로그램 정렬 기준
 * @param programA
 * @param programB
 * @param enrolmentType
 * @returns {number}
 */
function compareProgram(programA, programB, {enrolmentType, questionNo, isKeepOrder, prevProgramOrder}) {
    // 순서 유지 시 이전 순서대로
    if (isKeepOrder) {
        const orderA = prevProgramOrder[questionNo].findIndex(programNo => programNo === programA.programNo);
        const orderB = prevProgramOrder[questionNo].findIndex(programNo => programNo === programB.programNo);

        return orderA - orderB;
    }

    // 기준1. 신청여부
    if (programA.answer && !programB.answer) {
        return -1;
    }
    if (!programA.answer && programB.answer) {
        return 1;
    }

    //선착순인 경우
    if (enrolmentType === ENROLMENT_TYPE.FCFS) {
        // 기준1-1. 정원마감 여부 (아직 마감되지 않은 프로그램이 먼저 보임)
        if (!isFullAnswers(programA) && isFullAnswers(programB)) {
            return -1;
        }
        if (isFullAnswers(programA) && !isFullAnswers(programB)) {
            return 1;
        }
    }

    // 기준2. 당첨 여부
    if (enrolmentType === ENROLMENT_TYPE.DRAW_PRIOR) {
        if (programA.drawResult?.drawResultType === ANSWER_TYPE.REGULAR) {
            return -1;
        }
        if (programB.drawResult?.drawResultType === ANSWER_TYPE.REGULAR) {
            return 1;
        }
        if (programA.drawResult?.drawResultType === ANSWER_TYPE.RESERVED) {
            return -1;
        }
        if (programB.drawResult?.drawResultType === ANSWER_TYPE.RESERVED) {
            return 1;
        }
    }

    // 기준3. 지망순
    if (programA.answer && programB.answer) {
        return programA.answer.priority - programB.answer.priority;
    }

    // 기준4. 프로그램 넘버 (신청서 작성시 프로그램 순서)
    if (programA.programNumber < programB.programNumber) {
        return -1;
    }
    if (programA.programNumber > programB.programNumber) {
        return 1;
    }
    return 0;
}

/**
 * 해당 프로그램의 정원이 모두 찬 경우
 * @param limit
 * @param reservedLimit
 * @param answerTotalCount
 * @returns {boolean}
 */
function isFullAnswers({limit, reservedLimit, answerTotalCount}) {
    return limit + reservedLimit <= answerTotalCount;
}

/**
 * 변경된 프로그램 정보 적용
 * @param originSurvey
 * @param newData
 * @param programNo
 */
function changeProgramAnswer({originSurvey, newData, programNo}) {
    const originReceiver = originSurvey.receiver;
    const newAnswer = newData.surveyReceivers[0].surveyAnswers.find(answer => answer.questionNo === programNo);
    const newProgram = newData.surveyQuestions.find(question => question.questionNo === programNo);

    //신청상태 변경
    originSurvey.receiver.answerStateType = newData.surveyReceivers[0].answerStateType;

    //수신자 응답 변경
    if (originReceiver.surveyAnswers) {
        originReceiver.surveyAnswers = originReceiver.surveyAnswers.filter(answer => answer.questionNo !== programNo);
    }

    //신청추가한 경우만 해당 (신청취소한 경우는 해당하지 않음)
    if (newAnswer) {
        originReceiver.surveyAnswers.push({
            ...newAnswer
        });
    }

    //프로그램 정보 변경
    originSurvey.contents.questions.forEach(question => {
        let originProgramIndex = question.programs.findIndex(program => program.programNo === programNo);
        let originProgram = question.programs[originProgramIndex];

        if (originProgram) {
            question.programs[originProgramIndex] = {
                ...originProgram,
                answer: newAnswer && {
                    surveyAnswerNo: newAnswer.surveyAnswerNo,
                    surveyReceiverNo: newAnswer.surveyReceiverNo,
                    answerType: newAnswer.answer,
                    order: newAnswer.order
                },
                answerTotalCount: newProgram.count,
                answerRegularCount: newProgram.answerSummaries.find(summary => summary.answer === 'REGULAR').count,
                answerReservedCount: newProgram.answerSummaries.find(summary => summary.answer === 'RESERVED').count,
                isLoading: false,
            };

            question.programs = [...question.programs];
            question.choiceCount = question.programs.filter(program => program.answer).length;
        }
    });
}

/**
 * 에러없음 데이터 초기화
 * @returns {{errorType: null, resultCode: number, hasError: boolean, resultMessage: string}}
 */
function getNoError() {
    return {
        hasError: false,
        errorType: null,
        resultCode: 0,
        resultMessage: '',
    }
}

const receiveEnrolmentsAnswersForPreviewFromOtherWindow = ({previewMode}) => new Promise((resolve, reject) => {
    const messageHandler = event => {
        switch (event.data.type) {
            case 'GET_ENROLMENTS_ANSWERS_FOR_PREVIEW':
                resolve(event.data.data.previewData);
                window.removeEventListener('message', messageHandler);
                break;
            default:
                break
        }
    }

    window.addEventListener('message', messageHandler);

    // iframe에 속해있을 때
    if (previewMode === 'form') {
        // postMessage 먼저 보낼 때
        ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].forEach(origin => {
            window.parent.postMessage(
                {
                    type: 'BEFORE_ROUTE_ENTER_RESPONSE_OR_ENROLMENT_RESPONSE',
                    data: {
                        initFormData: getInitForm(),
                    },
                },
                origin,
            )
        })
    } else {
        // popup
        // postMessage 먼저 보낼 때
        ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].forEach(origin => {
            window.opener.postMessage(
                {
                    type: 'BEFORE_ROUTE_ENTER_RESPONSE_OR_ENROLMENT_RESPONSE',
                },
                origin,
            )
        })
    }
})

/**
 * 아이엠티처에서 노출되는 서식 / 일반 미리보기
 * @param previewMode
 * @returns {*}
 */
function getEnrolmentsAnswersForPreview({previewMode}) {
    return receiveEnrolmentsAnswersForPreviewFromOtherWindow({previewMode});
}

/**
 * 서식 미리보기 데이터 변환
 * @param data
 */
function getInitPreviewFormData(data) {
    return {
        title: data.title,
        surveyDesc: data.surveyDesc,
        schoolName: data.schoolName,
        receiverName: data.receiverName,
        enrolmentType: data.enrolmentType,
        contents: {
            images: data.images && data.images.map(image => {
                return {
                    imageNo: image.imageNo,
                    index: image.index,
                    url: image.url,
                };
            }),

            attachments: data.attachments && data.attachments.map(attachement => {
                return {
                    type: attachement.type,
                    description: attachement.description,
                    image: attachement.image && {
                        imageNo: attachement.image.imageNo,
                        url: attachement.image.url,
                    },
                    orderNo: attachement.orderNo,
                }
            }),

            documents: data.documents && data.documents.map(document => {
                return {
                    attachments: document.attachements && document.attachements.map(attachement => {
                        return {
                            type: attachement.type,
                            description: attachement.description,
                            image: attachement.image && {
                                imageNo: attachement.image.imageNo,
                                url: attachement.image.url,
                            },
                            orderNo: attachement.orderNo,
                        }
                    }),
                    description: document.description,
                    downloadUrlForExternal: document.downloadUrlForExternal,
                    fileName: document.fileName,
                    fileSize: document.fileSize,
                    orderNo: document.orderNo,
                    nowOrderNo: 0,
                }
            }),

            questions: data.questions
                .map(question => ({
                    question: question.question,
                    maxChoice: question.maxChoice,
                    choiceCount: 0,
                    questionNo: question.questionNo,
                    questionNumber: question.Number,
                    image: question.image && {
                        imageNo: question.image.imageNo,
                        index: question.image.index,
                        url: question.image.url,
                    },

                    programs: question.programs
                        .map(program => {
                            return {
                                programNo: program.programNo,
                                programNumber: program.programNumber,
                                programName: program.programName,
                                filters: program.filters,
                                limit: program.limit,
                                reservedLimit: program.reservedLimit,
                                answerTotalCount: 0,
                                answerRegularCount: 0,
                                answerReservedCount: 0,
                                times: program.times,
                                costs: program.costs,
                                isLoading: false,
                            };
                        })
                })),

            filterInfo: {
                type: data.filterInfo.type,
                filters: data.filterInfo.filters.map(filter => {
                    return {
                        label: filter.label,
                        value: filter.value,
                    }
                })
            },
        },
        startDate: data.startDate,
        endDate: data.endDate,
        sendDate: data.sendDate,
        receiver: {
            answerStateType: SURVEY_ANSWER_STATE_TYPE.NONRESPONSE,
        },
        answerStateType: SURVEY_ANSWER_STATE_TYPE.NONRESPONSE,
        options: [{
            optionType: 'MASKING',
            optionValue: 'N',
        }],
    };
}

/**
 * 일반 미리보기 데이터 변환
 * @param data
 */
function getInitPreviewData(previewData) {
    const contents = JSON.parse(previewData.survey.contents);
    return {
        title: previewData.survey.title,
        surveyDesc: previewData.survey.surveyDesc,
        schoolName: previewData.survey.schoolName,
        receiverName: previewData.survey.receiverName,
        enrolmentType: previewData.survey.questions.filter(question => !question.questionType || question.questionType === QUESTION_TYPE.ENROLMENT || question.questionType === QUESTION_TYPE.ENROLMENT_QUESTION)[0].questionOptions.find(option => option.optionType === 'ENROLMENT_TYPE').optionValue,

        contents: {
            type: contents.type,
            images: contents.images && contents.images.map(image => {
                return {
                    imageNo: image.imageNo,
                    index: image.index,
                    url: image.url,
                };
            }),

            attachments: previewData.survey.attachments && previewData.survey.attachments.map(attachement => {
                return {
                    type: attachement.type,
                    description: attachement.description,
                    image: attachement.image && {
                        imageNo: attachement.image.imageNo,
                        url: attachement.image.url,
                    },
                    orderNo: attachement.orderNo,
                }
            }),

            documents: previewData.survey.documents && previewData.survey.documents.map(document => {
                return {
                    attachments: document.attachements && document.attachements.map(attachement => {
                        return {
                            type: attachement.type,
                            description: attachement.description,
                            image: attachement.image && {
                                imageNo: attachement.image.imageNo,
                                url: attachement.image.url,
                            },
                            orderNo: attachement.orderNo,
                        }
                    }),
                    description: document.description,
                    downloadUrlForExternal: document.downloadUrlForExternal,
                    fileName: document.fileName,
                    fileSize: document.fileSize,
                    orderNo: document.orderNo,
                    nowOrderNo: 0,
                }
            }),

            agreements: contents.questions.filter(question => question.questionType === QUESTION_TYPE.ENROLMENT_AGREEMENT),

            questions: contents.questions.filter(question => !question.questionType || question.questionType === QUESTION_TYPE.ENROLMENT || question.questionType === QUESTION_TYPE.ENROLMENT_QUESTION)
                .map(question => ({
                    question: question.question,
                    maxChoice: question.maxChoice,
                    choiceCount: 0,
                    questionNo: question.questionNo,
                    questionNumber: question.Number,
                    image: question.image && {
                        imageNo: question.image.imageNo,
                        index: question.image.index,
                        url: question.image.url,
                    },

                    programs: question.programs
                        .map(program => {
                            return {
                                programNo: program.programNo,
                                programNumber: program.programNumber,
                                programName: program.programName,
                                programDesc: program.programDesc,
                                filters: program.filters,
                                limit: program.limit,
                                reservedLimit: program.reservedLimit,
                                answerTotalCount: 0,
                                answerRegularCount: 0,
                                answerReservedCount: 0,
                                times: program.times,
                                costs: program.costs,
                                isLoading: false,
                            };
                        })
                })),

            filterInfo: {
                type: contents.filterInfo.type,
                filters: contents.filterInfo.filters.map(filter => {
                    return {
                        label: filter.label,
                        value: filter.value,
                    }
                })
            },
        },

        startDate: previewData.survey.startDate,
        endDate: previewData.survey.endDate,
        sendDate: previewData.survey.sendDate,
        receiver: {
            answerStateType: SURVEY_ANSWER_STATE_TYPE.NONRESPONSE,
        },
        answerStateType: SURVEY_ANSWER_STATE_TYPE.NONRESPONSE,
        options: [{
            optionType: 'MASKING',
            optionValue: 'N',
        }],
    }
}

export default store;
